"use client";

import { TransportVoucherData } from "@/types/navigation";

// Formulaire d'édition du bon de transport. Présentationnel : reçoit l'état
// `editedData` et les callbacks du parent (TransportVoucherClient), qui garde la
// source de vérité, la validation et la sauvegarde. Le markup (classes, ids,
// libellés, ordre des champs) est reproduit à l'identique du monolithe d'origine
// — y compris les différences de styles entre sections (section entreprise via
// les styles `[&_input]` du <form>, autres sections en classes explicites).
type VoucherEditFormProps = {
  editedData: TransportVoucherData;
  onInputChange: (field: string, value: unknown) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
};

export const VoucherEditForm = ({
  editedData,
  onInputChange,
  onSubmit,
  onCancel,
}: VoucherEditFormProps) => (
  <div className="w-full lg:w-1/2">
    <form
      onSubmit={onSubmit}
      className="rounded-2xl border border-black/10 bg-white p-4 shadow-[0_18px_60px_-35px_rgba(0,0,0,0.45)] sm:p-6 [&_h3]:text-primary [&_input]:mt-1 [&_input]:block [&_input]:w-full [&_input]:rounded-md [&_input]:border [&_input]:!border-black/15 [&_input]:!bg-white [&_input]:px-3 [&_input]:py-2 [&_input]:!text-black [&_input]:placeholder:!text-black/40 [&_label]:!text-black/80 [&_select]:mt-1 [&_select]:block [&_select]:w-full [&_select]:rounded-md [&_select]:border [&_select]:!border-black/15 [&_select]:!bg-white [&_select]:px-3 [&_select]:py-2 [&_select]:!text-black"
      aria-label="Modifier les informations du bon de transport"
    >
      {/* Informations sur l'entreprise */}
      <div className="mb-6">
        <h3 className="mb-4 text-lg font-semibold text-primary">
          Informations sur l&apos;entreprise
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="entreprise" className="block text-sm font-medium">
              Entreprise
            </label>
            <input
              type="text"
              id="entreprise"
              name="entreprise"
              value={editedData.entreprise || ""}
              onChange={(e) => onInputChange("entreprise", e.target.value)}
              className=""
            />
          </div>
          <div>
            <label htmlFor="siren" className="block text-sm font-medium">
              SIREN
            </label>
            <input
              type="text"
              id="siren"
              name="siren"
              value={editedData.siren || ""}
              onChange={(e) => onInputChange("siren", e.target.value)}
              className=""
            />
          </div>
          <div>
            <label htmlFor="registreVTC" className="block text-sm font-medium">
              N° Registre VTC
            </label>
            <input
              type="text"
              id="registreVTC"
              name="registreVTC"
              value={editedData.registreVTC || ""}
              onChange={(e) => onInputChange("registreVTC", e.target.value)}
              className=""
            />
          </div>
          <div>
            <label htmlFor="telPro" className="block text-sm font-medium">
              Téléphone
            </label>
            <input
              type="tel"
              id="telPro"
              name="telPro"
              value={editedData.telPro || ""}
              onChange={(e) => onInputChange("telPro", e.target.value)}
              className=""
            />
          </div>
          <div>
            <label htmlFor="mailPro" className="block text-sm font-medium">
              Email
            </label>
            <input
              type="email"
              id="mailPro"
              name="mailPro"
              value={editedData.mailPro || ""}
              onChange={(e) => onInputChange("mailPro", e.target.value)}
              className=""
            />
          </div>
        </div>
      </div>

      {/* Informations sur le client */}
      <div className="mb-6">
        <h3 className="mb-4 text-lg font-semibold text-primary">
          Informations sur le client
        </h3>
        <div className="mb-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isEntreprise"
              name="isEntreprise"
              checked={editedData.isEntreprise || false}
              onChange={(e) => onInputChange("isEntreprise", e.target.checked)}
              className="h-4 w-4 rounded border-white/10 bg-background/10 text-primary focus:ring-primary"
            />
            <span className="text-sm font-medium text-black/80">
              Réservation pour une entreprise
            </span>
          </label>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {editedData.isEntreprise && (
            <>
              <div>
                <label
                  htmlFor="entrepriseName"
                  className="block text-sm font-medium"
                >
                  Nom de l&apos;entreprise
                </label>
                <input
                  type="text"
                  id="entrepriseName"
                  name="entrepriseName"
                  value={editedData.entrepriseName || ""}
                  onChange={(e) =>
                    onInputChange("entrepriseName", e.target.value)
                  }
                  className=""
                />
              </div>
              <div>
                <label htmlFor="siret" className="block text-sm font-medium">
                  SIRET/SIREN
                </label>
                <input
                  type="text"
                  id="siret"
                  name="siret"
                  value={editedData.siret || ""}
                  onChange={(e) => onInputChange("siret", e.target.value)}
                  placeholder="9 chiffres (SIREN) ou 14 chiffres (SIRET)"
                  className=""
                />
              </div>
            </>
          )}
          <div>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-white"
            >
              Prénom
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={editedData.firstName || ""}
              onChange={(e) => onInputChange("firstName", e.target.value)}
              className="mt-1 block w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-white/40"
            />
          </div>
          <div>
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-white"
            >
              Nom
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={editedData.lastName || ""}
              onChange={(e) => onInputChange("lastName", e.target.value)}
              className="mt-1 block w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-white/40"
            />
          </div>
          <div>
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-white"
            >
              Téléphone
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={editedData.phone || ""}
              onChange={(e) => onInputChange("phone", e.target.value)}
              className="mt-1 block w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-white/40"
            />
          </div>
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-white"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={editedData.email || ""}
              onChange={(e) => onInputChange("email", e.target.value)}
              className="mt-1 block w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-white/40"
            />
          </div>
        </div>
      </div>

      {/* Détails de la réservation */}
      <div className="mb-6">
        <h3 className="mb-4 text-lg font-semibold text-primary">
          Détails de la réservation
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label
              htmlFor="date"
              className="block text-sm font-medium text-white"
            >
              Date de prise en charge
            </label>
            <input
              type="date"
              id="date"
              name="date"
              value={editedData.date || ""}
              onChange={(e) => onInputChange("date", e.target.value)}
              className="mt-1 block w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-white/40"
            />
          </div>
          <div>
            <label
              htmlFor="time"
              className="block text-sm font-medium text-white"
            >
              Heure de prise en charge
            </label>
            <input
              type="time"
              id="time"
              name="time"
              value={editedData.time || ""}
              onChange={(e) => onInputChange("time", e.target.value)}
              className="mt-1 block w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-white/40"
            />
          </div>
          <div>
            <label
              htmlFor="departure"
              className="block text-sm font-medium text-white"
            >
              Lieu de prise en charge
            </label>
            <input
              type="text"
              id="departure"
              name="departure"
              value={editedData.departure || ""}
              onChange={(e) => onInputChange("departure", e.target.value)}
              className="mt-1 block w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-white/40"
            />
          </div>
          <div>
            <label
              htmlFor="arrival"
              className="block text-sm font-medium text-white"
            >
              Destination
            </label>
            <input
              type="text"
              id="arrival"
              name="arrival"
              value={editedData.arrival || ""}
              onChange={(e) => onInputChange("arrival", e.target.value)}
              className="mt-1 block w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-white/40"
            />
          </div>
          <div>
            <label
              htmlFor="passengers"
              className="block text-sm font-medium text-white"
            >
              Nombre de passagers
            </label>
            <input
              type="number"
              id="passengers"
              name="passengers"
              min="1"
              max="7"
              value={editedData.passengers || 1}
              onChange={(e) =>
                onInputChange("passengers", parseInt(e.target.value))
              }
              className="mt-1 block w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-white/40"
            />
          </div>
          <div>
            <label
              htmlFor="luggage"
              className="block text-sm font-medium text-white"
            >
              Nombre de bagages
            </label>
            <input
              type="number"
              id="luggage"
              name="luggage"
              min="0"
              max="10"
              value={editedData.luggage || 0}
              onChange={(e) =>
                onInputChange("luggage", parseInt(e.target.value))
              }
              className="mt-1 block w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-white/40"
            />
          </div>
        </div>
      </div>

      {/* Informations sur le véhicule et le chauffeur */}
      <div className="mb-6">
        <h3 className="mb-4 text-lg font-semibold text-primary">
          Informations sur le véhicule et le chauffeur
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label
              htmlFor="chauffeur"
              className="block text-sm font-medium text-white"
            >
              Chauffeur
            </label>
            <input
              type="text"
              id="chauffeur"
              name="chauffeur"
              value={editedData.chauffeur || ""}
              onChange={(e) => onInputChange("chauffeur", e.target.value)}
              className="mt-1 block w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-white/40"
            />
          </div>
          <div>
            <label
              htmlFor="vehicule"
              className="block text-sm font-medium text-white"
            >
              Véhicule
            </label>
            <input
              type="text"
              id="vehicule"
              name="vehicule"
              value={editedData.vehicule || ""}
              onChange={(e) => onInputChange("vehicule", e.target.value)}
              className="mt-1 block w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-white/40"
            />
          </div>
          <div>
            <label
              htmlFor="modele"
              className="block text-sm font-medium text-white"
            >
              Type de véhicule
            </label>
            <input
              type="text"
              id="modele"
              name="modele"
              value={editedData.modele || ""}
              onChange={(e) => onInputChange("modele", e.target.value)}
              className="mt-1 block w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-white/40"
            />
          </div>
          <div>
            <label
              htmlFor="immatriculation"
              className="block text-sm font-medium text-white"
            >
              Immatriculation
            </label>
            <input
              type="text"
              id="immatriculation"
              name="immatriculation"
              value={editedData.immatriculation || ""}
              onChange={(e) => onInputChange("immatriculation", e.target.value)}
              className="mt-1 block w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-white/40"
            />
          </div>
        </div>
      </div>

      {/* Détails tarifaires */}
      <div className="mb-6">
        <h3 className="mb-4 text-lg font-semibold text-primary">
          Détails tarifaires
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label
              htmlFor="estimatedPrice"
              className="block text-sm font-medium text-white"
            >
              Prix total
            </label>
            <input
              type="number"
              id="estimatedPrice"
              name="estimatedPrice"
              min="0"
              step="0.01"
              value={editedData.estimatedPrice || 0}
              onChange={(e) =>
                onInputChange("estimatedPrice", parseFloat(e.target.value))
              }
              className="mt-1 block w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-white/40"
            />
          </div>
          <div>
            <label
              htmlFor="selectedTVA"
              className="block text-sm font-medium text-white"
            >
              TVA
            </label>
            <select
              id="selectedTVA"
              name="selectedTVA"
              value={editedData.selectedTVA || ""}
              onChange={(e) => onInputChange("selectedTVA", e.target.value)}
              className="mt-1 block w-full rounded-md border border-white/10 bg-[#0f111a] px-3 py-2 text-white"
            >
              <option value="">Exonéré de TVA</option>
              <option value="5">TVA 5%</option>
              <option value="10">TVA 10%</option>
              <option value="20">TVA 20%</option>
            </select>
          </div>
          <div>
            <label
              htmlFor="modePaiement"
              className="block text-sm font-medium text-white"
            >
              Mode de paiement
            </label>
            <select
              id="modePaiement"
              name="modePaiement"
              value={editedData.modePaiement || ""}
              onChange={(e) => onInputChange("modePaiement", e.target.value)}
              className="mt-1 block w-full rounded-md border border-white/10 bg-[#0f111a] px-3 py-2 text-white"
            >
              <option value="CB">Carte bancaire</option>
              <option value="ESPECES">Espèces</option>
              <option value="VIREMENT">Virement</option>
            </select>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-4">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-black/15 bg-white px-4 py-2 text-sm font-medium text-black hover:border-primary"
          aria-label="Annuler les modifications"
        >
          Annuler
        </button>
        <button
          type="submit"
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-black hover:bg-primary/90"
          aria-label="Enregistrer les modifications"
        >
          Enregistrer
        </button>
      </div>
    </form>
  </div>
);
